import React, { useCallback, useMemo, useState } from 'react'
import dowArrow from '../../assets/img/flecha-abajo-responsive-mantenimiento.svg'
import { showGreyBackground } from '../utils/windows'

const formatMoney = (value) => `$${value}`

const normalizeText = (value) => (value ?? '').toString()

const safeTrim = (value) => normalizeText(value).trim()

const InfoBlock = ({ containerClassName = 'block-container', title, value, valueClassName = '', onValueClick }) => {
  return (
    <div className={containerClassName}>
      <div className="block-title">{title}</div>
      <div className={`block-value ${valueClassName}`.trim()} onClick={onValueClick}>
        {value}
      </div>
    </div>
  )
}

const getFechaBlocks = (aviso, isOrden) => {
  const mobileClassSuffix = isOrden ? 'fecha-orden-mobile' : ''
  const fechaAvisoOrdenTitle = isOrden ? 'Fecha de Orden:' : 'Fecha de Aviso:'
  const fechaAvisoOrdenValue = isOrden ? (aviso?.FECHA_ORDEN ?? '-') : (aviso?.FECHA_AVISO ?? '-')

  return [
    {
      key: 'fecha_aviso_orden',
      containerClassName: `block-container fecha-aviso-mobile ${mobileClassSuffix}`.trim(),
      title: fechaAvisoOrdenTitle,
      value: fechaAvisoOrdenValue
    },
    {
      key: 'fecha_cierre',
      containerClassName: `block-container fecha-cierre-mobile ${mobileClassSuffix}`.trim(),
      title: 'Fecha de Cierre:',
      value: aviso?.FECHA_CIERRE ?? '-'
    }
  ]
}

const getAvisoOnlyBlocks = (aviso) => {
  return [
    {
      key: 'prioridad',
      containerClassName: 'block-container prioridad',
      title: 'Prioridad:',
      value: aviso?.PRIORIDAD ?? '-'
    },
    {
      key: 'producto',
      containerClassName: 'block-container producto',
      title: 'Producto:',
      value: aviso?.PRODUCTO ?? '-'
    }
  ]
}

const getEquipoFacturaBlocks = (aviso, isOrden) => {
  const equipoEstadoTitle = isOrden ? 'Estado:' : 'Equipo:'
  const equipoEstadoValue = isOrden ? (aviso?.ESTADO ?? '-') : (aviso?.EQUIPO ?? '-')

  const facturaTrimmed = safeTrim(aviso?.FACTURA)
  const facturaValue = facturaTrimmed === '' ? '-' : aviso?.FACTURA
  const facturaOrMontoPlanValue = isOrden ? formatMoney(aviso?.MONTO_PLAN ?? 0) : facturaValue
  const facturaMontoPlanTitle = isOrden ? 'Monto Plan:' : 'Factura:'

  return [
    {
      key: 'equipo_estado',
      containerClassName: `block-container equipo ${isOrden ? 'equipo-orden' : ''}`,
      title: equipoEstadoTitle,
      value: equipoEstadoValue
    },
    {
      key: 'factura_monto_plan',
      containerClassName: 'block-container factura',
      title: facturaMontoPlanTitle,
      value: facturaOrMontoPlanValue
    }
  ]
}

const getContratistaFacturaBlock = (aviso, isOrden) => {
  const title = isOrden ? 'Factura:' : 'Contratista:'
  const value = isOrden ? (aviso?.FACTURA ?? '-') : (aviso?.CONTRATISTA ?? '-')

  return {
    key: 'contratista_factura',
    containerClassName: 'block-container contratista',
    title,
    value
  }
}

const HeaderInfo = ({ aviso, isOrden, isOrdenTablet, onOpenAvisoModal }) => {
  const blocks = useMemo(() => {
    const hasAdjuntos = Number(aviso?.TIENE_ADJUNTOS) > 0
    const nroTitle = isOrden ? 'N° de Orden:' : 'N° de Aviso:'
    const nroValue = isOrden ? Number(aviso?.NUMERO_ORDEN) : Number(aviso?.NUMERO_AVISO)

    const result = [
      {
        key: 'nro',
        containerClassName: 'block-container nro-aviso',
        title: nroTitle,
        value: nroValue,
        valueClassName: hasAdjuntos ? 'linked-value' : '',
        onValueClick: onOpenAvisoModal
      }
    ]

    if (isOrdenTablet) {
      result.push(
        {
          key: 'estado',
          title: 'Estado:',
          value: aviso?.ESTADO ?? '-'
        },
        {
          key: 'monto_plan',
          title: 'Monto plan:',
          value: formatMoney(aviso?.MONTO_PLAN ?? 0)
        }
      )
    }

    const ordenRaw = normalizeText(aviso?.NUMERO_ORDEN)
    const ordenValue = ordenRaw === '' ? '-' : aviso?.NUMERO_ORDEN
    const montoRealOrOrdenValue = isOrden ? formatMoney(aviso?.MONTO_REAL ?? 0) : ordenValue

    result.push({
      key: 'monto_real_or_orden',
      title: isOrden ? 'Monto Real:' : 'N° de Orden:',
      value: montoRealOrOrdenValue
    })

    if (!isOrden) {
      result.push(
        {
          key: 'fecha_aviso',
          containerClassName: 'block-container fecha-aviso',
          title: 'Fecha de Aviso:',
          value: aviso?.FECHA_AVISO ?? '-'
        },
        {
          key: 'fecha_cierre',
          containerClassName: 'block-container fecha-cierre',
          title: 'Fecha de Cierre:',
          value: aviso?.FECHA_CIERRE ?? '-'
        }
      )
    }

    return result
  }, [aviso, isOrden, isOrdenTablet, onOpenAvisoModal])

  return (
    <div className="info-container">
      {blocks.map((b) => (
        <InfoBlock
          key={b.key}
          containerClassName={b.containerClassName}
          title={b.title}
          value={b.value}
          valueClassName={b.valueClassName}
          onValueClick={b.onValueClick}
        />
      ))}
    </div>
  )
}

const InnerInfo = ({ aviso, isOrden, showEquipoFactura }) => {
  const blocks = useMemo(() => {
    const base = getFechaBlocks(aviso, isOrden)
    const avisoOnly = isOrden ? [] : getAvisoOnlyBlocks(aviso)
    const equipoFactura = showEquipoFactura ? getEquipoFacturaBlocks(aviso, isOrden) : []
    const tail = [getContratistaFacturaBlock(aviso, isOrden)]

    return [...base, ...avisoOnly, ...equipoFactura, ...tail]
  }, [aviso, isOrden, showEquipoFactura])

  return (
    <div className="info-container">
      {blocks.map((b) => (
        <InfoBlock
          key={b.key}
          containerClassName={b.containerClassName}
          title={b.title}
          value={b.value}
        />
      ))}
    </div>
  )
}

const MantenimientoResponsiveCard = ({
  isOrden = false,
  aviso,
  openFile: _openFile,
  setDisplayAvisoResponsiveModal,
  setOrdenResponsiveModal,
  setDistanceToTopResponsiveModal,
  setOpenedCards,
  openedCards
}) => {
  const [opened, setOpened] = useState(false)

  const width = window.innerWidth
  const isOrdenTablet = isOrden && width > 480 && width < 1023
  const showEquipoFactura = !isOrden || width < 480

  const toggleOpened = () => {
    setOpened((prev) => {
      !prev ? setOpenedCards(openedCards + 1) : setOpenedCards(openedCards - 1)
      return !prev
    })
  }

  const openAvisoResponsiveModal = useCallback(
    (e) => {
      const tieneAdjuntos = Number(aviso?.TIENE_ADJUNTOS) || 0
      if (tieneAdjuntos <= 0) return

      showGreyBackground()
      setDisplayAvisoResponsiveModal(true)
      setOrdenResponsiveModal(aviso)
      console.log('e.currentTarget.getBoundingClientRect().top', e.currentTarget.getBoundingClientRect().top)
      console.log('e.currentTarget.getBoundingClientRect().top  <= 680', e.currentTarget.getBoundingClientRect().top  <= 680)
      const distanceToTop = e.currentTarget.getBoundingClientRect().top  <= 680 ? e.currentTarget.getBoundingClientRect().top : e.currentTarget.getBoundingClientRect().top - 200
      setDistanceToTopResponsiveModal(distanceToTop)
    },
    [aviso, setDisplayAvisoResponsiveModal, setOrdenResponsiveModal, setDistanceToTopResponsiveModal]
  )

  return (
    <div className="mantenimiento-responsive-card-main-container">
      <div className="header-mantenimiento-responsive-card-container">
        <div className="hedaer-data-container">
          <div className="title-container">{aviso?.DESCRIPCION}</div>

          <HeaderInfo
            aviso={aviso}
            isOrden={isOrden}
            isOrdenTablet={isOrdenTablet}
            onOpenAvisoModal={openAvisoResponsiveModal}
          />
        </div>

        <img src={dowArrow} alt="dowArrow" onClick={toggleOpened} />
      </div>

      <div className={`responsive-mantenimiento-card-separator ${opened ? 'opened' : ''}`}></div>

      <div className={`inner-info-container ${opened ? 'opened' : 'closed'} ${isOrden ? 'inner-info-container-orden' : ''}`}>
        <InnerInfo aviso={aviso} isOrden={isOrden} showEquipoFactura={showEquipoFactura} />
      </div>
    </div>
  )
}

export default MantenimientoResponsiveCard
